/**
 * FRW Chat Widget — client.
 * Talks to /apps/chat/* (Shopify App Proxy → ESF apps/web).
 *
 * Customer-facing copy intentionally avoids "AI", "bot", "assistant", "agent".
 * The widget reads as a teammate replying in chat.
 */
(function () {
  'use strict';

  var root = document.getElementById('frw-chat-root');
  if (!root) return;
  if (root.dataset.frwChatBooted === '1') return;
  root.dataset.frwChatBooted = '1';

  var STORAGE_KEY = 'frw_chat_conversation_id';
  // Remembered captured email + a flag so we ask an anonymous visitor at most
  // once (after their first message), never re-prompting on return visits.
  var EMAIL_KEY = 'frw_chat_email';
  var EMAIL_ASKED_KEY = 'frw_chat_email_asked';
  // PROXY_BASE defaults to the Shopify App Proxy URL on production storefront.
  // For local dev, set data-proxy-base="http://localhost:3000/api/chat" on the
  // root element so the widget hits the dev server instead.
  var PROXY_BASE = root.dataset.proxyBase || '/apps/chat';
  // For local dev only: the storefront passes logged_in_customer_id via App
  // Proxy signing. On localhost we thread it through manually as a query param
  // so the backend's dev fallback (NODE_ENV !== production && no app proxy
  // secret) accepts it.
  var DEV_CUSTOMER_ID = root.dataset.devCustomerId || '';
  function withDevAuth(url) {
    if (!DEV_CUSTOMER_ID) return url;
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    return url + sep + 'logged_in_customer_id=' + encodeURIComponent(DEV_CUSTOMER_ID);
  }
  var loggedIn = root.dataset.loggedIn === '1';
  var customerName = root.dataset.customerName || '';

  // ── Build DOM ────────────────────────────────────────────────
  // Launcher is pre-rendered in the Liquid snippet so it paints with the page.
  // We just look it up (not create it) to avoid pop-in / CLS.
  var launcher = document.getElementById('frw-chat-launcher');
  if (!launcher) return; // snippet missing — bail rather than crash

  var drawer = document.createElement('div');
  drawer.className = 'frw-chat-drawer';
  drawer.innerHTML =
    '<div class="frw-chat-header">' +
      '<div>' +
        '<div class="frw-chat-header-title">Freedom Rave Wear Support</div>' +
        '<div class="frw-chat-header-sub">Usually replies in seconds</div>' +
      '</div>' +
      '<div class="frw-chat-header-actions">' +
        '<button class="frw-chat-handoff" aria-label="Email a teammate" title="Email a teammate">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>' +
        '</button>' +
        '<button class="frw-chat-close" aria-label="Close chat">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
      '</div>' +
    '</div>' +
    '<div class="frw-chat-body" id="frw-chat-body"></div>' +
    '<div class="frw-chat-typing" id="frw-chat-typing" style="display:none;">Typing...</div>' +
    '<div class="frw-chat-input-row">' +
      '<textarea class="frw-chat-input" id="frw-chat-input" rows="1" placeholder="' +
        'Type a message...' +
      '"></textarea>' +
      '<button class="frw-chat-send" id="frw-chat-send">Send</button>' +
    '</div>';

  // launcher already in DOM (server-rendered) — only append the drawer
  root.appendChild(drawer);

  var body = drawer.querySelector('#frw-chat-body');
  var input = drawer.querySelector('#frw-chat-input');
  var sendBtn = drawer.querySelector('#frw-chat-send');
  var typing = drawer.querySelector('#frw-chat-typing');
  var closeBtn = drawer.querySelector('.frw-chat-close');
  var handoffBtn = drawer.querySelector('.frw-chat-handoff');

  // ── State ────────────────────────────────────────────────────
  var conversationId = null;
  try { conversationId = localStorage.getItem(STORAGE_KEY); } catch (e) { /* private mode */ }
  var capturedEmail = null;
  try { capturedEmail = localStorage.getItem(EMAIL_KEY); } catch (e) { /* private mode */ }
  var emailAsked = false;
  try { emailAsked = localStorage.getItem(EMAIL_ASKED_KEY) === '1'; } catch (e) { /* private mode */ }

  // ── Open / close ─────────────────────────────────────────────
  launcher.addEventListener('click', function () {
    drawer.classList.add('is-open');
    setTimeout(function () { input.focus(); }, 100);
    if (body.children.length === 0) {
      seedGreeting();
      if (conversationId) loadHistory();
    }
  });
  closeBtn.addEventListener('click', function () {
    drawer.classList.remove('is-open');
  });
  if (handoffBtn) handoffBtn.addEventListener('click', offerHandoff);

  // ── Greeting ─────────────────────────────────────────────────
  function seedGreeting() {
    if (loggedIn) {
      var greetingText = customerName
        ? 'Hey ' + customerName + '! What can I help with?'
        : 'Hey! What can I help with?';
      appendBubble('agent', greetingText);
      return;
    }
    // Logged-out greeting: text + a login button so the customer can act
    // in one tap rather than hunting for a login link in the storefront nav.
    appendBubble('agent', "Hey! Ask about sizing, festivals, products, anything FRW. For your orders, log in below.");
    appendLoginCard({
      card: {
        title: 'Sign in to your FRW account',
        summary: 'Lets me pull up your orders and help with changes.',
        loginUrl: 'https://freedomravewear.com/account/login',
        confirmLabel: 'Log in',
      },
    });
  }

  // ── Append helpers ───────────────────────────────────────────
  function appendBubble(role, text) {
    var el = document.createElement('div');
    el.className = 'frw-chat-bubble frw-chat-bubble--' + role;
    el.textContent = text;
    body.appendChild(el);
    scrollToBottom();
    return el;
  }

  function appendCard(reply) {
    var card = document.createElement('div');
    card.className = 'frw-chat-card';
    card.innerHTML =
      '<div class="frw-chat-card-title"></div>' +
      '<div class="frw-chat-card-summary"></div>' +
      '<div class="frw-chat-card-detail"></div>' +
      '<div class="frw-chat-card-actions">' +
        '<button class="frw-chat-card-btn frw-chat-card-btn--cancel"></button>' +
        '<button class="frw-chat-card-btn frw-chat-card-btn--confirm"></button>' +
      '</div>';
    card.querySelector('.frw-chat-card-title').textContent = reply.card.title;
    card.querySelector('.frw-chat-card-summary').textContent = reply.card.summary;
    card.querySelector('.frw-chat-card-detail').textContent = reply.card.detail;
    var cancelBtn = card.querySelector('.frw-chat-card-btn--cancel');
    var confirmBtn = card.querySelector('.frw-chat-card-btn--confirm');
    cancelBtn.textContent = reply.card.cancelLabel || 'Cancel';
    confirmBtn.textContent = reply.card.confirmLabel || 'Confirm';

    cancelBtn.addEventListener('click', function () {
      cancelBtn.disabled = true; confirmBtn.disabled = true;
      card.querySelector('.frw-chat-card-actions').remove();
      appendBubble('system', 'Change cancelled.');
    });

    confirmBtn.addEventListener('click', function () {
      cancelBtn.disabled = true; confirmBtn.disabled = true;
      confirmBtn.textContent = 'Working...';
      confirmAction(reply.card.actionToken, function (result) {
        card.querySelector('.frw-chat-card-actions').remove();
        if (result.ok) {
          appendBubble('agent', result.summary || 'Done.');
        } else {
          appendBubble('refuse', result.error || 'Something went wrong.');
        }
      });
    });

    body.appendChild(card);
    scrollToBottom();
  }

  function scrollToBottom() {
    body.scrollTop = body.scrollHeight;
  }

  // ── Product recommendation cards ─────────────────────────────
  // Rendered from a message's structured content_blocks. Each product is a
  // tappable card (photo, name, price, availability) linking to the PDP.
  // Replaces the old experience of pasting raw URLs into a text bubble.
  function productStatusPill(status) {
    var map = {
      in_stock: ['In stock', 'instock'],
      made_to_order: ['Made to order', 'mto'],
      pre_order: ['Pre-order', 'preorder'],
      out_of_stock: ['Sold out', 'soldout'],
    };
    var entry = map[status];
    if (!entry) return null;
    var span = document.createElement('span');
    span.className = 'frw-chat-product-pill frw-chat-product-pill--' + entry[1];
    span.textContent = entry[0];
    return span;
  }

  function buildProductCard(p) {
    var a = document.createElement('a');
    a.className = 'frw-chat-product';
    a.href = p.url || '#';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';

    var thumb = document.createElement('div');
    thumb.className = 'frw-chat-product-thumb';
    if (p.imageUrl) {
      var img = document.createElement('img');
      img.src = p.imageUrl;
      img.alt = p.title || '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    }
    a.appendChild(thumb);

    var info = document.createElement('div');
    info.className = 'frw-chat-product-info';

    var title = document.createElement('div');
    title.className = 'frw-chat-product-title';
    title.textContent = p.title || '';
    info.appendChild(title);

    if (p.note) {
      var note = document.createElement('div');
      note.className = 'frw-chat-product-note';
      note.textContent = p.note;
      info.appendChild(note);
    }

    var meta = document.createElement('div');
    meta.className = 'frw-chat-product-meta';

    var price = document.createElement('span');
    price.className = 'frw-chat-product-price';
    price.textContent = p.price || '';
    meta.appendChild(price);

    if (p.compareAtPrice) {
      var was = document.createElement('span');
      was.className = 'frw-chat-product-was';
      was.textContent = p.compareAtPrice;
      meta.appendChild(was);
    }

    var pill = productStatusPill(p.status);
    if (pill) meta.appendChild(pill);

    info.appendChild(meta);
    a.appendChild(info);

    var chev = document.createElement('span');
    chev.className = 'frw-chat-product-chevron';
    chev.setAttribute('aria-hidden', 'true');
    chev.textContent = '›';
    a.appendChild(chev);

    return a;
  }

  function appendProducts(products) {
    if (!products || !products.length) return;
    var wrap = document.createElement('div');
    wrap.className = 'frw-chat-products';
    products.forEach(function (p) { wrap.appendChild(buildProductCard(p)); });
    body.appendChild(wrap);
    scrollToBottom();
  }

  // Render an ordered list of content blocks: text → agent bubble, runs of
  // consecutive product blocks → one card group.
  function renderBlocks(blocks) {
    var pending = [];
    function flush() {
      if (pending.length) { appendProducts(pending); pending = []; }
    }
    blocks.forEach(function (b) {
      if (b && b.type === 'product') {
        pending.push(b);
      } else if (b && b.type === 'text') {
        flush();
        if (b.text) appendBubble('agent', b.text);
      }
    });
    flush();
  }

  // ── Send ─────────────────────────────────────────────────────
  function send() {
    var text = input.value.trim();
    if (!text || sendBtn.disabled) return;

    // Whether this send is the one that creates the conversation — used to
    // prompt anonymous visitors for a follow-up email right after it lands.
    var firstSend = !conversationId;

    appendBubble('user', text);
    input.value = '';
    autosizeInput();
    sendBtn.disabled = true;
    typing.style.display = 'block';

    fetch(withDevAuth(PROXY_BASE + '/messages'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId: conversationId, message: text }),
      credentials: 'include',
    })
      .then(function (r) { return r.json().then(function (j) { return { status: r.status, body: j }; }); })
      .then(function (resp) {
        typing.style.display = 'none';
        sendBtn.disabled = false;
        if (resp.status >= 400) {
          appendBubble('refuse', (resp.body && resp.body.error) || 'Could not reach support. Try again in a moment.');
          return;
        }
        if (resp.body.conversationId) {
          conversationId = resp.body.conversationId;
          try { localStorage.setItem(STORAGE_KEY, conversationId); } catch (e) {}
        }
        renderReply(resp.body.reply);
        if (firstSend) maybeAskEmail();
      })
      .catch(function () {
        typing.style.display = 'none';
        sendBtn.disabled = false;
        appendBubble('refuse', 'Could not reach support. Check your connection and try again.');
      });
  }

  function renderReply(reply) {
    if (!reply) return;
    if (reply.kind === 'text') {
      if (reply.contentBlocks && reply.contentBlocks.length) {
        renderBlocks(reply.contentBlocks);
      } else {
        appendBubble('agent', reply.body);
      }
    } else if (reply.kind === 'refuse') {
      appendBubble('refuse', reply.body);
    } else if (reply.kind === 'action_card') {
      if (reply.body) appendBubble('agent', reply.body);
      appendCard(reply);
    } else if (reply.kind === 'login_prompt') {
      if (reply.body) appendBubble('agent', reply.body);
      appendLoginCard(reply);
    } else {
      appendBubble('agent', reply.body || '');
    }
  }

  // ── Login card ───────────────────────────────────────────────
  function appendLoginCard(reply) {
    var card = document.createElement('div');
    card.className = 'frw-chat-card';
    card.innerHTML =
      '<div class="frw-chat-card-title"></div>' +
      '<div class="frw-chat-card-summary"></div>' +
      '<div class="frw-chat-card-actions">' +
        '<button class="frw-chat-card-btn frw-chat-card-btn--confirm"></button>' +
      '</div>';
    card.querySelector('.frw-chat-card-title').textContent = reply.card.title;
    card.querySelector('.frw-chat-card-summary').textContent = reply.card.summary;
    var btn = card.querySelector('.frw-chat-card-btn--confirm');
    btn.textContent = reply.card.confirmLabel || 'Log in';
    btn.addEventListener('click', function () {
      // Append return_to so the customer comes back to where they were.
      var returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      var url = reply.card.loginUrl;
      var sep = url.indexOf('?') >= 0 ? '&' : '?';
      window.location.href = url + sep + 'return_to=' + returnTo;
    });
    body.appendChild(card);
    scrollToBottom();
  }

  // ── Follow-up email capture ──────────────────────────────────
  // Asked once, right after an anonymous visitor's first message — the moment
  // they've shown intent. Logged-in visitors already have a real email on the
  // conversation, so they're never asked. Skippable; the answer is remembered
  // so we never nag on return visits.
  function maybeAskEmail() {
    if (loggedIn) return;
    if (capturedEmail || emailAsked) return;
    if (!conversationId) return;
    emailAsked = true;
    try { localStorage.setItem(EMAIL_ASKED_KEY, '1'); } catch (e) {}
    // Small beat so it reads as a natural follow-up to the agent's reply.
    setTimeout(appendEmailCapture, 450);
  }

  function appendEmailCapture() {
    var card = document.createElement('div');
    card.className = 'frw-chat-card frw-chat-email-card';
    card.innerHTML =
      '<div class="frw-chat-card-title">Want a reply if we miss you?</div>' +
      '<div class="frw-chat-card-summary">Drop your email and we\'ll follow up directly, even if we\'re away when you message.</div>' +
      '<input type="email" class="frw-chat-email-input" inputmode="email" autocomplete="email" placeholder="you@email.com" aria-label="Your email" />' +
      '<div class="frw-chat-email-error" style="display:none;"></div>' +
      '<div class="frw-chat-card-actions">' +
        '<button class="frw-chat-card-btn frw-chat-card-btn--cancel">No thanks</button>' +
        '<button class="frw-chat-card-btn frw-chat-card-btn--confirm">Save</button>' +
      '</div>';

    var emailInput = card.querySelector('.frw-chat-email-input');
    var errEl = card.querySelector('.frw-chat-email-error');
    var cancelBtn = card.querySelector('.frw-chat-card-btn--cancel');
    var confirmBtn = card.querySelector('.frw-chat-card-btn--confirm');

    function closeActions() {
      var actions = card.querySelector('.frw-chat-card-actions');
      if (actions) actions.remove();
      if (emailInput) emailInput.disabled = true;
    }

    cancelBtn.addEventListener('click', function () {
      closeActions();
      appendBubble('system', 'No problem — ask away!');
    });

    function submitEmail() {
      var email = (emailInput.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errEl.textContent = 'Please enter a valid email.';
        errEl.style.display = 'block';
        emailInput.focus();
        return;
      }
      errEl.style.display = 'none';
      cancelBtn.disabled = true; confirmBtn.disabled = true;
      confirmBtn.textContent = 'Saving...';
      fetch(withDevAuth(PROXY_BASE + '/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationId: conversationId, email: email }),
        credentials: 'include',
      })
        .then(function (r) { return r.json().then(function (j) { return { status: r.status, body: j }; }); })
        .then(function (resp) {
          if (resp.status >= 400 || !(resp.body && resp.body.ok)) {
            cancelBtn.disabled = false; confirmBtn.disabled = false;
            confirmBtn.textContent = 'Save';
            errEl.textContent = (resp.body && resp.body.error) || 'Could not save. Try again.';
            errEl.style.display = 'block';
            return;
          }
          capturedEmail = email;
          try { localStorage.setItem(EMAIL_KEY, email); } catch (e) {}
          closeActions();
          appendBubble('agent', "Perfect — we'll reach you at " + email + " if we get disconnected. What's up?");
        })
        .catch(function () {
          cancelBtn.disabled = false; confirmBtn.disabled = false;
          confirmBtn.textContent = 'Save';
          errEl.textContent = 'Could not save. Check your connection and try again.';
          errEl.style.display = 'block';
        });
    }

    confirmBtn.addEventListener('click', submitEmail);
    emailInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitEmail(); }
    });

    body.appendChild(card);
    scrollToBottom();
    setTimeout(function () { try { emailInput.focus(); } catch (e) {} }, 50);
  }

  // ── Email-a-teammate handoff ─────────────────────────────────
  function offerHandoff() {
    // Need an active conversation with at least one message before there's
    // anything to hand off. Fall back to a hint if the customer hasn't
    // typed yet.
    if (!conversationId || body.children.length < 2) {
      appendBubble('agent', "Send me a quick note about what you need help with first, then I can email a teammate with the full context.");
      return;
    }
    var card = document.createElement('div');
    card.className = 'frw-chat-card';
    card.innerHTML =
      '<div class="frw-chat-card-title">Email a teammate</div>' +
      '<div class="frw-chat-card-summary">Sends this conversation to support@freedomravewear.com. They\'ll reply directly to your email.</div>' +
      '<div class="frw-chat-card-actions">' +
        '<button class="frw-chat-card-btn frw-chat-card-btn--cancel">Not now</button>' +
        '<button class="frw-chat-card-btn frw-chat-card-btn--confirm">Send to teammate</button>' +
      '</div>';
    var cancelBtn = card.querySelector('.frw-chat-card-btn--cancel');
    var confirmBtn = card.querySelector('.frw-chat-card-btn--confirm');
    cancelBtn.addEventListener('click', function () {
      cancelBtn.disabled = true; confirmBtn.disabled = true;
      card.querySelector('.frw-chat-card-actions').remove();
    });
    confirmBtn.addEventListener('click', function () {
      cancelBtn.disabled = true; confirmBtn.disabled = true;
      confirmBtn.textContent = 'Sending...';
      fetch(withDevAuth(PROXY_BASE + '/escalate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationId: conversationId }),
        credentials: 'include',
      })
        .then(function (r) { return r.json().then(function (j) { return { status: r.status, body: j }; }); })
        .then(function (resp) {
          card.querySelector('.frw-chat-card-actions').remove();
          if (resp.body && resp.body.ok) {
            appendBubble('agent', resp.body.summary || 'Sent to a teammate.');
          } else {
            appendBubble('refuse', (resp.body && resp.body.error) || 'Could not send the handoff. Email support@freedomravewear.com directly.');
          }
        })
        .catch(function () {
          card.querySelector('.frw-chat-card-actions').remove();
          appendBubble('refuse', 'Could not send the handoff. Email support@freedomravewear.com directly.');
        });
    });
    body.appendChild(card);
    scrollToBottom();
  }

  function confirmAction(actionToken, cb) {
    fetch(withDevAuth(PROXY_BASE + '/actions/confirm'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId: conversationId, actionToken: actionToken }),
      credentials: 'include',
    })
      .then(function (r) { return r.json().then(function (j) { return { status: r.status, body: j }; }); })
      .then(function (resp) { cb(resp.body || { ok: false, error: 'No response' }); })
      .catch(function () { cb({ ok: false, error: 'Could not reach support. Try again in a moment.' }); });
  }

  // ── History ──────────────────────────────────────────────────
  function loadHistory() {
    if (!conversationId) return;
    fetch(withDevAuth(PROXY_BASE + '/conversations/' + encodeURIComponent(conversationId)), {
      credentials: 'include',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.messages || !data.messages.length) return;
        // Clear seeded greeting; show real history.
        body.innerHTML = '';
        data.messages.forEach(function (m) {
          if (m.contentBlocks && m.contentBlocks.length) {
            renderBlocks(m.contentBlocks);
          } else {
            appendBubble(m.role === 'user' ? 'user' : 'agent', m.body);
          }
        });
      })
      .catch(function () { /* silent — fall back to seeded greeting */ });
  }

  // ── Input handling ───────────────────────────────────────────
  function autosizeInput() {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 120) + 'px';
  }
  input.addEventListener('input', autosizeInput);
  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  });
  sendBtn.addEventListener('click', send);

  // Signal init complete + flush any queued early click from the snippet's
  // pre-script so the chat opens immediately if a fast user clicked before
  // the JS finished parsing.
  window.frwChatReady = true;
  if (window.frwChatPendingOpen) {
    window.frwChatPendingOpen = false;
    launcher.click();
  }
})();
